import {
  PDF_MIN_SPLIT_REMAINDER,
  PDF_PAGE_CONTENT_BOTTOM,
  PDF_PAGE_CONTENT_HEIGHT,
  PdfPageCursor,
} from './pdfPage';
import {
  countPdfRichTextLines,
  measurePdfTextWidth,
  PdfFont,
  pdfLineHeight,
  regularFont,
  wrapPdfText,
} from './pdfTextMetrics';

/**
 * A frontend replica of the PDF service layout items (`app/domain/pdf/creator/item`): each block knows its
 * height and how the renderer places it — moved to the next page as a whole, broken line by line, or
 * laid out child by child. Only geometry is kept; nothing is drawn.
 */
export interface PdfBlock {
  /** Total height including margins. */
  readonly height: number;
  /** Whether the parent column leaves the page break to the block instead of moving it as a whole. */
  breaksItself(cursor: PdfPageCursor): boolean;
  place(cursor: PdfPageCursor, placement: PdfPlacement): void;
}

export type PdfPlacement = {
  /**
   * A normal table row reserves its height up front and its content never breaks; a row taller than the
   * remaining page is split and its cells lay out like free columns (`Table.drawTallRow`).
   */
  inTableCell: boolean;
};

const FREE_PLACEMENT: PdfPlacement = { inTableCell: false };

export type PdfSpacing = {
  top: number;
  right: number;
  bottom: number;
  left: number;
};

export function pdfSpacing(spacing: Partial<PdfSpacing> = {}): PdfSpacing {
  return { top: 0, right: 0, bottom: 0, left: 0, ...spacing };
}

export function uniformPdfSpacing(value: number): PdfSpacing {
  return { top: value, right: value, bottom: value, left: value };
}

const horizontal = (spacing: PdfSpacing): number => spacing.left + spacing.right;
const vertical = (spacing: PdfSpacing): number => spacing.top + spacing.bottom;

const sumHeights = (blocks: PdfBlock[]): number =>
  blocks.reduce((total, block) => total + block.height, 0);

/** Text, image, grid row: always moved to the next page as a whole. */
class AtomicBlock implements PdfBlock {
  constructor(readonly height: number) {}

  breaksItself(): boolean {
    return false;
  }

  place(cursor: PdfPageCursor): void {
    cursor.y += this.height;
  }
}

/** `RichText`: outside a table row a block that does not fit is continued line by line on the next page. */
class RichTextBlock implements PdfBlock {
  readonly height: number;

  constructor(
    private readonly lineCount: number,
    private readonly lineHeight: number,
    private readonly margin: PdfSpacing
  ) {
    this.height = vertical(margin) + lineCount * lineHeight;
  }

  breaksItself(cursor: PdfPageCursor): boolean {
    return (
      this.height > PDF_PAGE_CONTENT_HEIGHT ||
      cursor.available >= PDF_MIN_SPLIT_REMAINDER
    );
  }

  place(cursor: PdfPageCursor, placement: PdfPlacement): void {
    if (placement.inTableCell) {
      cursor.y += this.height;
      return;
    }
    cursor.y += this.margin.top;
    for (let line = 0; line < this.lineCount; line += 1) {
      if (cursor.y + this.lineHeight > PDF_PAGE_CONTENT_BOTTOM) {
        cursor.advancePage();
      }
      cursor.y += this.lineHeight;
    }
    cursor.y += this.margin.bottom;
  }
}

/** `Column`: children are placed one by one; a child that does not fit starts the next page. */
class ColumnBlock implements PdfBlock {
  readonly height: number;

  constructor(
    protected readonly children: PdfBlock[],
    protected readonly margin: PdfSpacing
  ) {
    this.height = vertical(margin) + this.contentHeight;
  }

  protected get contentHeight(): number {
    return sumHeights(this.children);
  }

  breaksItself(): boolean {
    return true;
  }

  place(cursor: PdfPageCursor, placement: PdfPlacement): void {
    cursor.y += this.margin.top;
    this.children.forEach((child) => {
      if (!child.breaksItself(cursor) && !cursor.isAtPageTop) {
        cursor.advanceIfLessThan(child.height);
      }
      child.place(cursor, placement);
    });
    cursor.y += this.margin.bottom;
  }
}

/** `SinglePageColumn`: starts on the next page when its content does not fit in the remaining room. */
class SinglePageColumnBlock extends ColumnBlock {
  place(cursor: PdfPageCursor, placement: PdfPlacement): void {
    if (
      !placement.inTableCell &&
      !cursor.isAtPageTop &&
      cursor.available - this.margin.top < this.contentHeight
    ) {
      cursor.advancePage();
    }
    super.place(cursor, placement);
  }
}

/** `NextPage`: a forced page break, ignored at the very top of a page. */
class NextPageBlock implements PdfBlock {
  readonly height = 0;

  breaksItself(): boolean {
    return true;
  }

  place(cursor: PdfPageCursor): void {
    if (!cursor.isAtPageTop) {
      cursor.advancePage();
    }
  }
}

/** `TableRow`: as tall as its tallest cell; each cell is a padded column. */
class TableRowBlock {
  readonly height: number;

  constructor(private readonly cells: ColumnBlock[]) {
    this.height = Math.max(0, ...cells.map((cell) => cell.height));
  }

  /** `Table.drawTallRow`: every cell flows from the same start; the row ends where the longest one does. */
  placeSplit(cursor: PdfPageCursor): void {
    const end = this.cells.reduce((furthest, cell) => {
      const cellCursor = cursor.clone();
      cell.place(cellCursor, FREE_PLACEMENT);
      return furthest.isBefore(cellCursor) ? cellCursor : furthest;
    }, cursor.clone());
    cursor.moveTo(end);
  }
}

/** `Table`: rows never break unless taller than the remaining page; the header row repeats on each page. */
class TableBlock implements PdfBlock {
  readonly height: number;

  constructor(
    private readonly headerRow: TableRowBlock | undefined,
    private readonly rows: TableRowBlock[],
    private readonly margin: PdfSpacing
  ) {
    this.height =
      vertical(margin) +
      (headerRow?.height ?? 0) +
      rows.reduce((total, row) => total + row.height, 0);
  }

  breaksItself(): boolean {
    return true;
  }

  place(cursor: PdfPageCursor): void {
    cursor.y += this.margin.top;
    if (this.headerRow) {
      this.placeRow(this.headerRow, cursor, undefined);
    }
    this.rows.forEach((row) => this.placeRow(row, cursor, this.headerRow));
    cursor.y += this.margin.bottom;
  }

  private placeRow(
    row: TableRowBlock,
    cursor: PdfPageCursor,
    repeatedHeader: TableRowBlock | undefined
  ): void {
    const fitsNowhere = row.height > PDF_PAGE_CONTENT_HEIGHT;
    const worthSplitting =
      row.height > cursor.available && cursor.available >= PDF_MIN_SPLIT_REMAINDER;
    if (fitsNowhere || worthSplitting) {
      row.placeSplit(cursor);
      return;
    }
    if (cursor.available < row.height) {
      cursor.advancePage();
      cursor.y += repeatedHeader?.height ?? 0;
    }
    cursor.y += row.height;
  }
}

/** A piece of content of a known width: builders get the width from their container. */
export type PdfContent = PdfBlock | undefined;

const present = (blocks: PdfContent[]): PdfBlock[] =>
  blocks.filter((block): block is PdfBlock => block !== undefined);

export type PdfTextOptions = {
  font?: PdfFont;
  margin?: Partial<PdfSpacing>;
  /** Value drawn after the caption, inline when it fits on the caption's last line (`Text.layOutPostfix`). */
  postfix?: string;
};

export function pdfText(
  text: string,
  width: number,
  { font = regularFont(), margin: marginParams, postfix }: PdfTextOptions = {}
): PdfBlock {
  const margin = pdfSpacing(marginParams);
  const maxWidth = width - horizontal(margin);
  const lines = wrapPdfText(text, maxWidth, font);
  return new AtomicBlock(
    vertical(margin) +
      (lines.length + postfixLineCount(lines, postfix, maxWidth, font)) *
        pdfLineHeight(font)
  );
}

function postfixLineCount(
  captionLines: string[],
  postfix: string | undefined,
  maxWidth: number,
  font: PdfFont
): number {
  if (!postfix) {
    return 0;
  }
  const postfixFont = regularFont(font.size);
  const lines = wrapPdfText(postfix, maxWidth, postfixFont);
  const lastCaptionLineWidth = measurePdfTextWidth(
    captionLines[captionLines.length - 1] ?? '',
    font
  );
  const inline =
    lines.length === 1 &&
    lastCaptionLineWidth + measurePdfTextWidth(` ${lines[0]}`, postfixFont) <= maxWidth;
  return inline ? 0 : lines.length;
}

export function pdfRichText(
  html: string,
  width: number,
  { font = regularFont(), margin: marginParams }: Omit<PdfTextOptions, 'postfix'> = {}
): PdfBlock {
  const margin = pdfSpacing(marginParams);
  const lineCount = countPdfRichTextLines(html, width - horizontal(margin), font);
  return new RichTextBlock(lineCount, pdfLineHeight(font), margin);
}

export function pdfFixedBlock(height: number): PdfBlock {
  return new AtomicBlock(height);
}

export function pdfColumn(
  children: PdfContent[],
  margin?: Partial<PdfSpacing>
): PdfBlock {
  return new ColumnBlock(present(children), pdfSpacing(margin));
}

export function pdfSinglePageColumn(
  children: PdfContent[],
  margin?: Partial<PdfSpacing>
): PdfBlock {
  return new SinglePageColumnBlock(present(children), pdfSpacing(margin));
}

export function pdfNextPage(): PdfBlock {
  return new NextPageBlock();
}

/** Whether the block occupies room — an empty attachments column must not create a table row. */
export function isRenderablePdfBlock(block: PdfContent): block is PdfBlock {
  return block !== undefined && block.height > 0;
}

/** Default `tableCellPadding` of the PDF service. */
export const PDF_TABLE_CELL_PADDING = 4;

export type PdfTableCell = {
  colSpan?: number;
  padding?: PdfSpacing;
  /** Builds the cell content for the width left inside the cell padding. */
  content: (contentWidth: number) => PdfContent[];
};

export type PdfTableSpec = {
  columnRatios: number[];
  rows: PdfTableCell[][];
  headerRow?: PdfTableCell[];
  margin?: Partial<PdfSpacing>;
};

function buildTableRow(
  cells: PdfTableCell[],
  columnWidths: number[]
): TableRowBlock {
  let column = 0;
  return new TableRowBlock(
    cells.map(({ colSpan = 1, padding, content }) => {
      const cellWidth = columnWidths
        .slice(column, column + colSpan)
        .reduce((total, width) => total + width, 0);
      column += colSpan;
      const cellPadding = padding ?? uniformPdfSpacing(PDF_TABLE_CELL_PADDING);
      return new ColumnBlock(
        present(content(cellWidth - horizontal(cellPadding))),
        pdfSpacing({ top: cellPadding.top, bottom: cellPadding.bottom })
      );
    })
  );
}

export function pdfTable(spec: PdfTableSpec, width: number): PdfBlock {
  const ratioTotal = spec.columnRatios.reduce((total, ratio) => total + ratio, 0);
  const columnWidths = spec.columnRatios.map((ratio) => (ratio / ratioTotal) * width);
  return new TableBlock(
    spec.headerRow && buildTableRow(spec.headerRow, columnWidths),
    spec.rows.map((row) => buildTableRow(row, columnWidths)),
    pdfSpacing(spec.margin)
  );
}

/** Pages the renderer needs for the blocks laid out one after another from the top of the first page. */
export function countPdfPages(blocks: PdfBlock[]): number {
  const cursor = new PdfPageCursor();
  pdfColumn(blocks).place(cursor, FREE_PLACEMENT);
  return cursor.page;
}
